import React from 'react'
import PropTypes from 'prop-types'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import GolfCourse from '@material-ui/icons/GolfCourse'
import CalendarToday from '@material-ui/icons/CalendarToday'
import { makeStyles } from '@material-ui/core/styles'
import { useRouter } from 'next/router'

const useStyles = makeStyles(theme => ({
  clickableCard: {
    cursor: 'pointer',
    height: '100%',
  },
  featuredCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '16px',
    overflow: 'hidden',
    background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${theme.palette.grey[50]} 100%)`,
    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
    transition: 'transform 0.3s ease-in-out',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-8px)',
    },
  },
  cardMedia: {
    height: 100,
    position: 'relative',
    background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
    flexShrink: 0,
    minHeight: 100,
    maxHeight: 100,
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
  },
  contentWrapper: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  golferName: {
    fontWeight: 700,
    fontSize: '1.5rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(1),
    color: theme.palette.secondary.main,
    textAlign: 'center',
  },
  achievementDate: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    background: theme.palette.grey[100],
    borderRadius: '6px',
  },
  courseSection: {
    background: theme.palette.grey[50],
    borderRadius: '8px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[200]}`,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  courseName: {
    fontWeight: 600,
    fontSize: '1.4rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.secondary.main,
  },
  holeNumber: {
    textAlign: 'center',
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(2),
  },
  shotDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: theme.spacing(1),
    marginTop: 'auto',
  },
  shotDetail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(1),
    background: theme.palette.common.white,
    borderRadius: '6px',
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  shotDetailLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 600,
  },
  shotDetailValue: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.secondary.main,
    marginTop: theme.spacing(0.5),
  },
  placeholderImage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
    color: theme.palette.common.white,
  },
  placeholderIcon: {
    fontSize: '4rem',
    opacity: 0.7,
  },
}))

const HoleInOneCard = ({ holeInOne }) => {
  const styles = useStyles()
  const router = useRouter()

  const {
    id,
    golferName,
    courseName,
    holeNumber,
    yardage,
    clubUsed,
    occurredAt,
  } = holeInOne

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleCardClick = () => {
    router.push(`/hole-in-one/${id}`)
  }

  const renderCardMedia = () => {
    return (
      <Box className={`${styles.cardMedia} ${styles.placeholderImage}`}>
        <img
          src="/images/logos/pga-of-america-logo-white-on-transparent.png"
          alt="PGA of America"
          style={{ width: '100px', height: 'auto', opacity: 0.7 }}
        />
      </Box>
    )
  }

  return (
    <Box className={styles.clickableCard} onClick={handleCardClick}>
      <Card className={styles.featuredCard}>
        {renderCardMedia()}

        <CardContent className={styles.cardContent}>
          <Box className={styles.contentWrapper}>
            <Typography className={styles.golferName}>
              {golferName}
            </Typography>

            <Box className={styles.achievementDate}>
              <CalendarToday style={{ fontSize: '1.2rem', marginRight: '8px' }} />
              {formatDate(occurredAt)}
            </Box>

            <Box className={styles.courseSection}>
              <Typography className={styles.courseName}>
                <GolfCourse style={{ fontSize: '1.5rem', marginRight: '8px' }} />
                {courseName}
              </Typography>

              <Typography className={styles.holeNumber}>
                Hole #{holeNumber}
              </Typography>

              <Box className={styles.shotDetails}>
                {yardage && (
                  <Box className={styles.shotDetail}>
                    <Typography className={styles.shotDetailLabel}>Distance</Typography>
                    <Typography className={styles.shotDetailValue}>{yardage} yds</Typography>
                  </Box>
                )}
                {clubUsed && (
                  <Box className={styles.shotDetail}>
                    <Typography className={styles.shotDetailLabel}>Club</Typography>
                    <Typography className={styles.shotDetailValue}>{clubUsed}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

HoleInOneCard.propTypes = {
  holeInOne: PropTypes.shape({
    id: PropTypes.string.isRequired,
    golferName: PropTypes.string,
    courseName: PropTypes.string,
    holeNumber: PropTypes.number,
    yardage: PropTypes.number,
    clubUsed: PropTypes.string,
    occurredAt: PropTypes.string,
    description: PropTypes.string,
    featuredPhotoUrl: PropTypes.string,
  }).isRequired,
}

export default HoleInOneCard
